'use client'

import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// `primary` added for brand-token-migration's "you"/recommended-emphasis
// concept (design source: lime-900 is "the only lime safe as text", used for
// the "You" chip/"Recommended" marker precedent) — distinct from the four
// semantic-status hues above (positive/negative/warning/info). Named
// `primary`, never `lime` — components reach for the semantic role, not the
// raw palette name, so a future rebrand only touches the token alias.
type TagTone = 'neutral' | 'indigo' | 'emerald' | 'rose' | 'amber' | 'primary'
type TagSize = 'sm' | 'md' | 'lg'
type TagVariant = 'pill' | 'modern'

interface TagProps {
  tone?: TagTone
  /**
   * Which of the master's Types this renders (untitledui-reference.md "Badge",
   * master `12:539`). The master has three — `Pill color` (full radius),
   * `Badge color` (6–8px), `Badge modern` (white shell, 1px `#d4d4d4`, colour
   * only in the dot). We implement two:
   *
   * - `pill` (default) — `Pill color`, the soft tinted fill at full radius.
   * - `modern` — `Badge modern`: white fill, hairline border, `#404040` label,
   *   badge radius rather than full. This is what the onboarding mock uses for
   *   chips **inside an input** (the alias and own-domain rows), where a tinted
   *   pill would compete with the field it sits in. `tone` and `size` do not
   *   apply — this type renders one fixed geometry, not the sm/md/lg ladder.
   *
   * Measured from node `223:4477` (fileKey `whU5iJG8jgidUGsTzRPmO4`, step-2
   * "Also known as" field): padding 8px left / 4px right (4px right when
   * `onRemove` is absent — 8px each side, symmetric) / 3px vertical, label
   * 12px/18px/500, gap 3px, close glyph 10px in a 2px pad with a 3px
   * **rounded-square** radius (not the pill's circle).
   *
   * Radius is `rounded-sm`, which IS the kit's 6px as of 2026-08-11 —
   * `--radius-sm` had been aliased to `md` (8px), leaving no 6px step at all;
   * realigning the ramp to the kit's 6/8/12 made this exact rather than a
   * divergence.
   *
   * Recorded divergences (the kit's own raw literals, not its tokens):
   * - Height hits the spec's 24px exactly via `h-6` (on-scale) rather than
   *   deriving it from the kit's 3px vertical padding, which has no scale
   *   step of its own — using a fixed height avoids inventing a fourth
   *   off-scale value for a number (`py-[3px]`) the geometry never actually
   *   needs.
   * - The label's 18px line-height is `leading-normal` (1.5 × the 12px
   *   `text-xs` size = 18px) — an existing on-scale utility, not a new token
   *   (ARC-22: `theme.text` gains no entry because none is needed).
   * - The internal gap is `gap-1` (4px) where Figma exported `gap-[3px]`, and
   *   the close button is `rounded-xs` (4px) where Figma exported
   *   `rounded-[3px]`. Both of those are RAW literals in the mock, not kit
   *   tokens — the same chip's other gap uses `spacing-xs` (4px), which is the
   *   real token. Matching 4px is therefore closer to the design system than
   *   copying the designer's one-off 3px would be.
   */
  variant?: TagVariant
  /**
   * Height/pad step from the master's Pill row — sm 22px, md 24px, lg 28px.
   * Defaults to sm. Ignored by `variant="modern"`, which renders one fixed
   * 24px geometry — see the `variant` doc above.
   */
  size?: TagSize
  /**
   * Adds the master's 1px border, present at every type, size and colour there
   * (untitledui-reference.md "Badge", master `12:539`). Off by default — most
   * call sites want the plain soft fill.
   */
  bordered?: boolean
  className?: string
  children: React.ReactNode
  /**
   * When provided, renders an X button that calls this handler on click.
   * The button stops event propagation so parent click handlers are unaffected.
   * Use for removable chips (e.g. filter tags, applied tag lists).
   */
  onRemove?: () => void
  /** Accessible label for the remove button. Defaults to "Remove". */
  removeLabel?: string
}

// -50 fill / -700 text steps of the master's ramp.
const TAG_CLASS: Record<TagTone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  indigo: 'bg-info-soft text-info',
  emerald: 'bg-emerald-bg text-emerald',
  rose: 'bg-rose-bg text-rose',
  // `text-amber-text`, not `text-amber`: the hue token measured 2.76:1 on `--amber-bg` (axe
  // color-contrast, 12px); the text token is 5.17:1 light and the same value the dark Badge uses.
  amber: 'bg-amber-bg text-amber-text',
  // primary-100 = "soft fill, selected row" per the design source; -900 is
  // the only step safe as text (--on-lime/--primary-foreground is for text
  // ON a --primary fill specifically, not for primary-colored text on a
  // light background — a different role, same underlying hue today).
  primary: 'bg-primary-100 text-primary-900',
}

// -200 border step. Same one-token-per-hue convention as badge.tsx's accent
// variants: the base hue token at low alpha stands in for the kit's separate
// -200 step (no second ramp invented).
const TAG_BORDER_CLASS: Record<TagTone, string> = {
  neutral: 'border-border',
  indigo: 'border-info/20',
  emerald: 'border-emerald/20',
  rose: 'border-rose/20',
  amber: 'border-amber/20',
  primary: 'border-primary-500/30',
}

// Height/pad columns of the master's Pill row (untitledui-reference.md
// "Badge"): sm 22 / md 24 / lg 28, pad 8 / 10 / 12. 22px has no Tailwind
// scale step, hence the one arbitrary height value.
const TAG_SIZE_CLASS: Record<TagSize, string> = {
  sm: 'h-[22px] px-2 text-xs',
  md: 'h-6 px-2.5 text-sm',
  lg: 'h-7 px-3 text-sm',
}

/**
 * Tag — category pill atom, borderless (default) or bordered.
 *
 * Use for CATEGORIES ("Your brand", "Corporate", engine labels, topic tags).
 * Do NOT use for status — use <StatusBadge> for state (it has a dot + state
 * semantics). The master folds category and state into one Icon axis; we
 * keep <Tag> and <StatusBadge> split per DES-05 — a confirmed correct
 * divergence, not a gap.
 *
 * `bordered` adds the master's 1px border with its three-step ramp: `-50`
 * fill, `-200` border, `-700` text. Borderless is the plain soft `*-bg` fill.
 * No shadows either way. No dot.
 *
 * Atoms own no layout margins — spacing is the parent's job.
 *
 * Optional `onRemove` prop adds an X button for removable chip variants.
 * When `onRemove` is absent, appearance and API are unchanged.
 *
 * The remove control renders as `<Button variant="unstyled" size="auto">`
 * (ButtonPrimitive-migration, 2026-08-30) — confirmed against the Claude
 * Design source's own `Tag.jsx`, which renders Tag as a plain `<span>` and
 * its own remove affordance as a `<span role="button">`, not their `Button`
 * component. That agrees with DES-05: Tag is category, not control, so its
 * removal glyph does not pick up a real Button *variant* (default/outline/
 * etc.) — it stays a bare, fully custom-styled `<button>` element, using the
 * atom only for native button semantics, exactly as `ButtonPrimitive` did.
 */
export function Tag({
  tone = 'neutral',
  size = 'sm',
  variant = 'pill',
  bordered = false,
  className,
  children,
  onRemove,
  removeLabel = 'Remove',
}: TagProps): React.ReactElement {
  const isModern = variant === 'modern'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 border font-medium whitespace-nowrap',
        isModern
          ? // `Badge modern` — white shell, hairline border, #404040 label,
            // one fixed 24px geometry. Always bordered: the border IS the
            // type, so `bordered` is moot. `pl-2 pr-1` (8px/4px) makes room
            // for the close button; without one, `px-2` keeps both sides 8px.
            cn(
              'h-6 rounded-sm border-border bg-card text-xs leading-normal text-foreground-secondary',
              onRemove ? 'pl-2 pr-1' : 'px-2'
            )
          : cn(
              'rounded-full',
              bordered ? TAG_BORDER_CLASS[tone] : 'border-transparent',
              TAG_CLASS[tone],
              TAG_SIZE_CLASS[size],
              onRemove && 'pr-1'
            ),
        className
      )}
    >
      {children}
      {onRemove && (
        <Button
          type="button"
          variant="unstyled"
          size="auto"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label={removeLabel}
          className={cn(
            'p-0.5 hover:bg-black/10 dark:hover:bg-white/10 duration-[var(--duration-base)]',
            // Badge modern's close affordance is a rounded SQUARE (kit: 3px
            // radius — nearest scale step is `--radius-xs`, 4px, a 1px
            // divergence). Every other variant keeps the full circle.
            isModern ? 'rounded-xs' : 'rounded-full'
          )}
        >
          <X className="size-2.5" aria-hidden="true" />
        </Button>
      )}
    </span>
  )
}
